// Read-only mock of the customer-facing enrollment screen the owner sees in
// the builder's "Enroll" preview tab. Mirrors the FORM page of the real
// EnrollPage (apps/web/src/enroll/EnrollPage.tsx): logo + business name
// header, the enabled onboarding fields (with asterisks for required, and
// birthday as month/day dropdowns), a consent checkbox, and a "Sign up"
// submit button. Inputs are disabled — this is a preview, not a live form.
//
// Deliberately NO Add-to-Google-Wallet button: that button only appears on
// the SUCCESS screen the customer sees after submitting the form, not on
// the form itself. Showing it here would misrepresent the journey. The
// owner can extend this later to preview the success state if they want
// to see the wallet button in context — for now, the form is what step 1
// actually controls, so that's what the preview shows.
//
// Re-renders whenever the owner toggles a field or renames the program,
// so they see the customer's first-touch experience instantly.

import type { OnboardingFieldKey } from '@naya/shared'

export function EnrollmentPreview({
  businessName,
  programName,
  branchName,
  logoUrl,
  fields,
  referralEnabled,
}: {
  businessName: string
  programName: string
  /** Branch name appears in the real subtitle ("...at <branch name>."); the
   *  builder doesn't have a specific branch context, so the caller passes a
   *  generic placeholder (the tenant's first branch in practice, or just the
   *  business name for the single-branch case). */
  branchName: string
  logoUrl: string | null
  fields: Array<{ key: OnboardingFieldKey; label: string; required: boolean }>
  /** When the program runs a referral, the real enroll form shows a collapsed
   *  "Have a referral code?" row between the fields and consent — mirror it. */
  referralEnabled: boolean
}) {
  return (
    <div className="enroll-preview-wrap" role="region" aria-label="Customer enrollment screen preview">
      <div className="enroll-preview-card">
        <div className="enroll-preview-header">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="enroll-preview-logo" />
          ) : (
            <span className="enroll-preview-logo-fallback" aria-hidden="true" />
          )}
          <h2 className="enroll-preview-title">{businessName || 'Your business'}</h2>
          <p className="enroll-preview-subtitle">
            Sign up for the {programName || 'Rewards'} loyalty program at{' '}
            {branchName || businessName || 'this branch'}.
          </p>
        </div>

        {/* The form. Inputs are disabled — pointer-events:none in CSS
            backs this up so click-throughs don't bubble to the wizard. */}
        <div className="enroll-preview-form" aria-hidden="true">
          {fields.length === 0 ? (
            <p className="enroll-preview-empty">
              No fields collected at enrollment — the customer only checks the
              consent box.
            </p>
          ) : (
            fields.map((f) => (
              <div key={f.key} className="enroll-preview-field">
                <label>
                  {f.label}
                  {f.required && <span className="enroll-preview-required">*</span>}
                </label>
                {f.key === 'birthday' ? (
                  <div className="enroll-preview-birthday">
                    <div className="enroll-preview-select">Month</div>
                    <div className="enroll-preview-select">Day</div>
                  </div>
                ) : (
                  <div className="enroll-preview-input">
                    {placeholderFor(f.key)}
                  </div>
                )}
              </div>
            ))
          )}

          {referralEnabled && (
            <div className="enroll-preview-referral">
              <span>Have a referral code?</span>
              <span
                className="enroll-preview-referral-chevron"
                aria-hidden="true"
              >
                ⌄
              </span>
            </div>
          )}

          <div className="enroll-preview-consent">
            <span className="enroll-preview-checkbox" aria-hidden="true" />
            <span className="enroll-preview-consent-text">
              I agree to the program's terms and privacy notice.
            </span>
          </div>

          {/* Submit button styled to mirror the real EnrollPage's .enroll-submit
              (terracotta brand fill, white text, "Sign up" copy). The rolling
              "Saving your details… → Creating your loyalty card…" labels only
              appear during submission on the real page; the preview shows the
              idle copy. */}
          <button type="button" className="enroll-preview-submit-button" disabled>
            Sign up
          </button>
        </div>
      </div>
    </div>
  )
}

function placeholderFor(key: OnboardingFieldKey): string {
  switch (key) {
    case 'first_name':
      return 'Juan'
    case 'last_name':
      return 'Dela Cruz'
    case 'email':
      return 'juan@email.com'
    case 'phone':
      return '09112223333'
    case 'birthday':
      // Unreachable — birthday renders the two-dropdown variant above.
      return ''
  }
}
